import React, { useState, useRef, useCallback, useEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";

import CollectionSelector from "./CollectionSelector";
import Modal from "./Modal";
import ModalFooterCloseBtn from "./ModalFooterCloseBtn";
import quickCloze from "./quickCloze";
import Toast from "./Toast";

function hasCloze(text) {
  return /\{\{.+\}\}/.test(text);
}

function renderClozePreview(text) {
  const match = text.match(/^(.*?)\{\{(.+?)\}\}(.*)$/s);
  if (!match) return text;
  return <>{match[1]}<strong><u>{match[2]}</u></strong>{match[3]}</>;
}

export default function AddToCollectionLight({ text: initialText, translation: initialTranslation, collectionsUrl, quickClozeUrl, onClose, onSaved }) {
  const [collection, setCollection] = useState(null);
  const [editedText, setEditedText] = useState(initialText || "");
  const [editedTranslation, setEditedTranslation] = useState(initialTranslation || "");
  const [editingText, setEditingText] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const modalRef = useRef(null);
  const isClosedRef = useRef(false);

  useEffect(() => () => { isClosedRef.current = true; }, []);

  const handleClozeSelect = useCallback(() => {
    const selection = window.getSelection();
    const str = selection.toString().trim();
    if (!str) return;

    const plainText = editedText.replace(/\{\{|\}\}/g, "");
    if (plainText.includes(str)) {
      setEditedText(plainText.replace(str, "{{" + str + "}}"));
    }
  }, [editedText]);

  const handleSave = useCallback(async () => {
    if (!collection) return;

    setSaving(true);

    let text = editedText;
    if (!hasCloze(text)) {
      try {
        text = await quickCloze(quickClozeUrl, text, { isCancelled: () => isClosedRef.current });
      } catch {
        text = "";
      }
      if (isClosedRef.current) return;
      if (!hasCloze(text)) {
        setSaving(false);
        alert("We couldn't pick a missing word for this sentence. Please select/highlight one.");
        return;
      }
      setEditedText(text);
    }

    $.ajax({
      contentType: "application/json",
      data: JSON.stringify({
        collection_cloze_sentence: { text, translation: editedTranslation }
      }),
      dataType: "json",
      method: "post",
      url: collection.collectionClozeSentencesUrl
    })
      .done((data) => {
        if (data.errors) {
          setSaving(false);
          alert(data.errors);
          return;
        }
        setSaved(true);
        setTimeout(() => {
          if (modalRef.current) {
            modalRef.current.hide();
          }
          onSaved && onSaved();
        }, 1500);
      })
      .fail((_jqXHR, textStatus, errorThrown) => {
        setSaving(false);
        alert(`Error adding sentence: ${textStatus} ${errorThrown}. Please try again.`);
      });
  }, [collection, editedText, editedTranslation, onSaved, quickClozeUrl]);

  const textValid = hasCloze(editedText);
  const canPickCloze = !!quickClozeUrl;

  return (
    <Modal
      ref={modalRef}
      title="Add to Collection"
      onHidden={onClose}
      footer={
        <>
          <ModalFooterCloseBtn />
        </>
      }
    >
      <p className="text-secondary" style={{ marginBottom: 12 }}>
        {textValid
          ? "Cloze set. Select/highlight different text to change it."
          : canPickCloze
            ? "Select/highlight text to use as the missing word, or save and we'll pick one."
            : "Select/highlight text to use as the missing word."
        }
      </p>

      <hr />
      <div style={{ marginBottom: 6 }}>
        {/*<label className="text-secondary">Sentence</label>*/}
        {editingText ? (
          <div style={{ marginBottom: 8 }}>
            <TextareaAutosize
              className="form-control"
              minRows={1}
              autoFocus
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
            />
            <button className="btn btn-default btn-xs" style={{ marginTop: 6 }} onClick={() => setEditingText(false)}>Done</button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <p
              style={{ fontSize: "1.25em", cursor: "text", userSelect: "text", flex: 1, marginBottom: 0 }}
              onMouseUp={handleClozeSelect}
            >
              {renderClozePreview(editedText)}
            </p>
            <button className="btn btn-default btn-xs" style={{ whiteSpace: "nowrap" }} onClick={() => setEditingText(true)}>Edit</button>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 15 }}>
        {/*<label className="text-secondary">Translation</label>*/}
        {editingTranslation ? (
          <div>
            <TextareaAutosize
              className="form-control"
              minRows={1}
              autoFocus
              value={editedTranslation}
              onChange={(e) => setEditedTranslation(e.target.value)}
            />
            <button className="btn btn-default btn-xs" style={{ marginTop: 6 }} onClick={() => setEditingTranslation(false)}>Done</button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <p className="text-secondary" style={{ flex: 1, marginBottom: 0 }}>
              {editedTranslation || <span className="text-muted">No translation</span>}
            </p>
            <button className="btn btn-default btn-xs" style={{ whiteSpace: "nowrap" }} onClick={() => setEditingTranslation(true)}>Edit</button>
          </div>
        )}
      </div>

      <hr />
      <CollectionSelector
        collectionsUrl={collectionsUrl}
        onSelect={(c) => setCollection(c)}
      />
      {saved && <Toast content="Saved! &#x1f389;" context="success" />}

      <div style={{ marginTop: 16 }}>
        <button
            className="btn btn-success joystix btn-lg btn-block"
            disabled={saving || saved || !collection || !(textValid || canPickCloze)}
            onClick={handleSave}
          >
            {saving ? "Saving..." : saved ? "Saved!" : "Save"}
          </button>
      </div>
    </Modal>
  );
}
